/* eslint-disable */
// /src/pages/PointsHistoryPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import PageTopBar from "../components/PageTopBar";
import { useDriverStatusContext } from "../context/DriverStatusContext";

import {
    fetchMemberPointsBalance,
    listMemberPointsLedger,
    resolveMemberDocId,
} from "../services/pointsService";

import Spinner from "../components/Spinner";

/* ===== 공통 스타일 (MyOrdersPage 톤 그대로) ===== */

const Wrap = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #d7e8ff 0%, #eaf3ff 100%);
  display: flex;
  flex-direction: column;
`;

const Sheet = styled.div`
  background: #f7faff;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 16px 20px;
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
`;

const Sub = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
`;

const SummaryCard = styled.div`
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 12px 14px;
  box-shadow: 0 3px 14px rgba(15, 23, 42, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const BalanceLabel = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const BalanceValue = styled.div`
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div`
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 12px 14px;
  box-shadow: 0 3px 14px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RowTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const RowTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.4;
`;

const Delta = styled.div`
  font-size: 14px;
  font-weight: 900;
  color: ${(p) => (p.$neg ? "#b91c1c" : "#166534")};
`;

const Meta = styled.div`
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
  white-space: pre-line;
`;

const EmptyBox = styled.div`
  margin-top: 30px;
  padding: 24px 14px;
  border-radius: 16px;
  border: 1px dashed #d1d5db;
  background: #ffffff;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.7;
`;

const Center = styled.div`
  padding: 22px 0;
  display: flex;
  justify-content: center;
`;

function formatP(n = 0) {
    const v = Number(n || 0);
    return `${v.toLocaleString("ko-KR")}P`;
}

function formatDate(ts) {
    if (!ts) return "-";
    try {
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        return `${y}.${m}.${day} ${hh}:${mm}`;
    } catch {
        return "-";
    }
}
function pickReason(raw) {
    const rt = String(raw?.reasonType || raw?.type || "").trim();

    // ✅ 주문에서 포인트 사용이면: orderTitleSnapshot 우선
    if (rt === "ORDER_USE") {
        const t = String(raw?.orderTitleSnapshot || raw?.orderTitle || "").trim();
        if (t) return t;
    }

    // ✅ 일반 케이스도 orderTitleSnapshot 있으면 우선 노출
    const snap = String(raw?.orderTitleSnapshot || "").trim();
    if (snap) return snap;

    return (
        String(
            raw?.reason ||
            raw?.title ||
            raw?.memo ||
            raw?.desc ||
            raw?.type ||
            ""
        ).trim() || "포인트 변동"
    );
}


export default function PointsHistoryPage() {
    const { phoneE164, loading: driverLoading } = useDriverStatusContext();

    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(0);
    const [ledger, setLedger] = useState([]);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function run() {
            if (!phoneE164) {
                setLoading(false);
                setBalance(0);
                setLedger([]);
                return;
            }

            setLoading(true);
            setErr("");

            try {
                const memberDocId = await resolveMemberDocId({ phoneE164 });

                const balRes = await fetchMemberPointsBalance({ memberDocId, phoneE164 });
                const histRes = await listMemberPointsLedger({ memberDocId, phoneE164, limitCount: 200 });

                if (cancelled) return;

                setBalance(Number(balRes?.balance || 0) || 0);
                setLedger(Array.isArray(histRes?.items) ? histRes.items : []);
            } catch (e) {
                console.error("[PointsHistoryPage] load error", e);
                if (!cancelled) {
                    setErr(e?.message || "포인트 내역을 불러오지 못했습니다.");
                    setBalance(0);
                    setLedger([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (!driverLoading) run();

        return () => {
            cancelled = true;
        };
    }, [phoneE164, driverLoading]);

    const viewRows = useMemo(() => {
        const list = Array.isArray(ledger) ? ledger : [];
        return list.map((r) => {
            const delta = Number(r?.delta ?? r?.amount ?? r?.pointsDelta ?? 0);
            const after =
                r?.balanceAfter != null
                    ? Number(r.balanceAfter)
                    : r?.afterBalance != null
                        ? Number(r.afterBalance)
                        : r?.balance != null
                            ? Number(r.balance)
                            : null;

            const createdAt = r?.createdAt || r?.created || r?.timestamp || r?.time || null;

            return {
                delta: Number.isFinite(delta) ? delta : 0,
                after: Number.isFinite(after) ? after : after === 0 ? 0 : null,
                createdAt,
                reason: pickReason(r),
                type: String(r?.reasonType || r?.type || "").trim(),
                source: String(r?.source || "").trim(),
            };
        });
    }, [ledger]);

    const showSpinner = driverLoading || loading;

    return (
        <Wrap>
            <Sheet>
                <PageTopBar variant="back" label="뒤로가기" />

                <Body>
                    <Title>포인트 사용 내역</Title>
                    <Sub>포인트 적립/차감 내역을 확인할 수 있습니다.</Sub>

                    <SummaryCard>
                        <div>
                            <BalanceLabel>보유 포인트</BalanceLabel>
                            <div style={{ height: 4 }} />
                            <BalanceValue>{formatP(balance)}</BalanceValue>
                        </div>
                        <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "right" }}>
                            {phoneE164 ? "회원 확인됨" : "회원 정보 없음"}
                        </div>
                    </SummaryCard>

                    {showSpinner ? (
                        <Center>{Spinner ? <Spinner /> : <div>불러오는 중…</div>}</Center>
                    ) : err ? (
                        <EmptyBox>{err}</EmptyBox>
                    ) : !phoneE164 ? (
                        <EmptyBox>
                            로그인 정보를 찾을 수 없습니다.
                            <br />
                            다시 로그인 후 이용해 주세요.
                        </EmptyBox>
                    ) : viewRows.length === 0 ? (
                        <EmptyBox>포인트 내역이 없습니다.</EmptyBox>
                    ) : (
                        <List>
                            {viewRows.map((r, idx) => {
                                const neg = r.delta < 0;
                                const deltaText = r.delta === 0 ? "0P" : `${neg ? "" : "+"}${formatP(r.delta)}`;

                                const metaLines = [
                                    `일시: ${formatDate(r.createdAt)}`,
                                    r.type ? `구분: ${r.type}` : "",
                                    r.source ? `출처: ${r.source}` : "",
                                    r.after != null ? `변동 후 잔액: ${formatP(r.after)}` : "",
                                ]
                                    .filter(Boolean)
                                    .join("\n");

                                return (
                                    <Row key={`${idx}_${formatDate(r.createdAt)}`}>
                                        <RowTop>
                                            <RowTitle>{r.reason}</RowTitle>
                                            <Delta $neg={neg}>{deltaText}</Delta>
                                        </RowTop>
                                        <Meta>{metaLines}</Meta>
                                    </Row>
                                );
                            })}
                        </List>
                    )}
                </Body>
            </Sheet>
        </Wrap>
    );
}
